import { addListener, removeListener } from "./support/utils";

class BRModal
{
    // Registry to hold widget instances indexed by DOM elements
    static modalInstances = new Map();

    constructor(modalElement, options={})
    {
        this.brModal = modalElement;
        this.options = options;
        this.brModalDismiss = modalElement.querySelectorAll('[data-br-modal-dismiss]');
        this.brModalDialog = modalElement.querySelector('[data-br-modal-dialog]');
        this.brModalTitle = modalElement.querySelector('.modalTitle');
        this.brModalBody = modalElement.querySelector('.modalBody');
        this.brModalFooter = modalElement.querySelector('.modalFooter');

        this.hide = this.hide.bind(this);
        this.show = this.show.bind(this);
        this.executeFunction = this.executeFunction.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);

        // Initialize the instance-specific event listeners map
        this.instanceEventListeners = new Map();

        this.setListener('click', this.brModalDismiss, this.hide);

        BRModal.modalInstances.set(modalElement, this);

        this.createEvents();
    }

    createEvents()
    {
        // Prepare a function to dynamically create the event
        this.createModalShowEvent = (opener) => new CustomEvent('show.br.modal', {
            detail: {
                openModal: this.brModal,
                opener: opener,
            }
        });

        this.createModalHideEvent = (opener) => new CustomEvent('hide.br.modal', {
            detail: {
                openModal: this.brModal,
                opener: opener,
            }
        });
    }

    setListener(event, elements, method)
    {
        if (elements instanceof NodeList || Array.isArray(elements)) {
            elements.forEach(element => this._addListenerToElement(event, element, method));
        } else {
            this._addListenerToElement(event, elements, method);
        }
    }

    _addListenerToElement(event, element, method)
    {
        addListener(event, element, method);

        // Check if the element already has listeners stored
        if (!this.instanceEventListeners.has(element)) {
            this.instanceEventListeners.set(element, new Map());
        }

        const listenersMap = this.instanceEventListeners.get(element);

        // Use a combination of event type and method as the key for the inner map
        // This assumes we won't add the exact same function twice as a listener for the same event type on the same element
        const eventKey = `${event}_${method.name}`;

        listenersMap.set(eventKey, method);
    }

    removeListener(element, event, method)
    {
        if (this.instanceEventListeners.has(element)) {
            const listenersMap = this.instanceEventListeners.get(element);
            const eventKey = `${event}_${method.name}`;
            if (listenersMap.has(eventKey)) {
                removeListener(event, element, listenersMap.get(eventKey));
                listenersMap.delete(eventKey);
            }

            // If there are no more listeners for this element, remove the element from the map
            if (listenersMap.size === 0) {
                this.instanceEventListeners.delete(element);
            }
        }
    }

    executeFunction(e)
    {
        let btn = e.currentTarget;

        if ( typeof this.callback === 'function') {
            // The callback is responsible for closing the BRModal.
            this.callback(btn);
        }
        else {
            this.hide(e);
        }
    }

    setButtonFunction(callback)
    {
        this.callback = callback;
    }

    show(evt)
    {
        if (evt) {
            // Create the event with current information
            this.modalShow = this.createModalShowEvent(evt.currentTarget);
            // Fire the show.br.modal event
            this.brModal.dispatchEvent(this.modalShow);
        }

        // Set the body styles
        const body = document.querySelector('body');
        body.style.overflow = 'hidden';
        body.style.paddingRight = '15px';
        body.setAttribute('data-br-modal-open', '');

        // Add the background
        if ( this.brModal.nextElementSibling === null || this.brModal.nextElementSibling.getAttribute('data-br-backdrop') === null ) {
            const newElement = document.createElement('div');
            newElement.setAttribute('class', 'opacity-50 transition-all duration-300 ease-in-out fixed top-0 left-0 z-[1338] bg-black w-screen h-screen');
            newElement.setAttribute('data-br-backdrop', '');
            this.brModal.insertAdjacentElement('afterend', newElement);
        }

        // Add default button click methods
        if (this.brModalFooter) {
            if ( !this.brModalFooter.classList.contains('hidden') ) {
                this.brModalFooter.querySelectorAll('button').forEach(button => {
                    addListener('click', button, this.executeFunction);
                });
            }
        }

        if (this.options.closeClickOutside && this.options.closeClickOutside === true) {
            addListener('click', document, this.onDocumentClick);
        }

        // Show the elements
        this.brModal.classList.remove('hidden');
        this.brModalDialog.classList.remove('opacity-0');
    }

    hide(evt)
    {
        if (evt) {
            // Create the event with current information
            this.modalHide = this.createModalHideEvent(evt.currentTarget);
            // Fire the show.br.modal event
            this.brModal.dispatchEvent(this.modalHide);
        }

        // Remove the body styles
        const body = document.querySelector('body');
        body.style.overflow = '';
        body.style.paddingRight = '';
        body.removeAttribute('data-br-modal-open');

        // this.brModalDialog.classList.remove('opacity-100');
        this.brModalDialog.classList.add('opacity-0');
        this.brModal.classList.add('hidden');

        // Remove the background
        if ( this.brModal.nextElementSibling !== null && this.brModal.nextElementSibling.getAttribute('data-br-backdrop') !== null ) {
            this.brModal.nextElementSibling.remove();
        }

        if (this.options.closeClickOutside && this.options.closeClickOutside === true) {
            removeListener('click', document, this.onDocumentClick);
        }

        const modalId = this.brModal.getAttribute('id');
        if (modalId.startsWith('SPIN')) {
            this.dispose();
            document.getElementById(modalId)?.remove();
        }
    }

    onDocumentClick(evt)
    {
        if (!this.brModalBody.parentElement.contains(evt.target) && evt.target !== this.options.openerElement) {
            this.hide(evt);
        }
    }

    setTitle(value)
    {
        this.brModalTitle.innerHTML = value;
    }

    setBetweenTitleBody(element)
    {
        this.brModalBody.parentElement.insertBefore(element, this.brModalBody);
    }

    setBody(value)
    {
        this.brModalBody.innerHTML = value;
    }

    /**
     *
     * @param which
     * @param classNames Separated by space
     * @param btnLabel
     */
    setFooterButton(which, classNames, btnLabel)
    {
        const classArray = classNames.split(/\s+/);
        this.brModalFooter.children[which].style.display = 'block';

        for(let i in classArray) {
            this.brModalFooter.children[which].classList.add(classArray[i]);
        }

        if (btnLabel !== undefined) {
            this.brModalFooter.children[which].innerHTML = btnLabel;
        }
    }

    hideFooterButton(which)
    {
        this.brModalFooter.children[which].style.display = 'none';
    }

    showFooterButton(which)
    {
        this.brModalFooter.children[which].style.display = 'block';
    }

    showFooter()
    {
        this.brModalFooter.classList.remove('hidden');
    }

    hideFooter()
    {
        this.brModalFooter.classList.add('hidden');
    }

    dispose()
    {
        // Remove all event listeners associated with this instance
        for (const [element, listenersMap] of this.instanceEventListeners) {
            for (const [eventKey, method] of listenersMap) {
                const [event, methodName] = eventKey.split('_');
                removeListener(event, element, method);
            }
        }

        // Clear the instanceEventListeners map
        this.instanceEventListeners.clear();
        this.instanceEventListeners = null;

        BRModal.modalInstances.delete(this.brModal);
        this.brModal = null;
        this.brModalDismiss = null;
        this.brModalDialog = null;
        this.brModalTitle = null;
        this.brModalBody = null;
        this.brModalFooter = null;
    }

    static initAll()
    {
        document.querySelectorAll('[data-br-modal]').forEach(modalElement => {
            if (!BRModal.modalInstances.has(modalElement)) {
                new BRModal(modalElement);
            }
        });
    }

    static initToggles(parent)
    {
        const container = parent || document;
        container.querySelectorAll('[data-br-toggle="modal"]').forEach(toggle => {
            const target = toggle.getAttribute('data-br-target');
            const targetModal = document.querySelector(target);
            const brModal = BRModal.getOrCreateInstance(targetModal);

            // Set the toggle listener
            brModal.setListener('click', toggle, (evt) => brModal.show(evt));
        });
    }

    // Static method to access a widget instance from a DOM element
    static getInstance(element)
    {
        return BRModal.modalInstances.get(element);
    }

    static getOrCreateInstance( element )
    {
        let brModal = BRModal.getInstance(element);
        if (!brModal) {
            brModal = new BRModal(element);
        }

        return brModal;
    }

    static closeModal( selector )
    {
        const modalEl = document.querySelector(selector);
        const modal = BRModal.getInstance(modalEl);
        if (modal) modal.hide();
    }
}

export default BRModal;